import React, { useState, useEffect, useCallback, useRef } from "react";
import { Typography, notification, Spin } from "antd";
import "../styles.scss";
import { useNavigate } from "react-router-dom";
import ShoppingList from "./ShoppingList";
import MarketPlaceService from "../../../utils/api/marketplace";
import LayoutPage from "../../../layouts/Main";
import { CartCache } from "../../../utils/cartCache";
import useAppStore from "../../../store/useAppStore";

export default function ShoppingCart(props) {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const isGuest = !user?.id;
  const cartKey = isGuest ? "cart_guest" : `cart_${user.id}_${user.role}`;
  const isUpdatingRef = useRef(false);
  const initialLoadRef = useRef(true);
  const fetchInProgressRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const updateCart = useAppStore((state) => state.updateCart);
  const storeCart = useAppStore((state) => state.cart);

  const [loading, setLoading] = useState(true);

  const getVisibleProducts = useCallback(
    (products) => {
      if (!Array.isArray(products)) return [];

      return products.filter((product) => {
        if (!product || typeof product !== "object") return false;

        const productUserId = product.user_id ?? product.userId;
        const productUserRole = product.user_role ?? product.userRole;

        // Keep items when metadata is missing to avoid dropping valid cart rows.
        const idMatches =
          productUserId == null || String(productUserId) === String(user?.id);
        const roleMatches =
          productUserRole == null ||
          String(productUserRole) === String(user?.role);

        return idMatches && roleMatches;
      });
    },
    [user?.id, user?.role]
  );

  const getQueryParams = (url) => {
    let queryParams = {};
    let queryString = url.split("?")[1]?.split("#")[0];
    if (queryString) {
      queryString.split("&").forEach((param) => {
        let [key, value] = param.split("=");
        queryParams[key] = decodeURIComponent(value);
      });
    }
    return queryParams;
  };

  const { checkout_id, order_id, payment_status } = getQueryParams(
    window.location.href
  );

  const processSuccessfulPayment = useCallback(
    async (cartData) => {
      try {
        const newOrder = {
          client_id: cartData.client_id,
          client_name: cartData.client_info?.name || user.name,
          client_bussiness: cartData.client_info?.bussiness || "",
          client_fcm: cartData.client_info?.fcmToken || "",
          products: cartData.products,
          price: cartData.price,
          change_type: cartData.change_type,
          status: 1,
        };

        await MarketPlaceService.deleteCart(cartData.id);
        await MarketPlaceService.newOrder(newOrder);

        updateCart({ products: [] });

        localStorage.removeItem(cartKey);
        CartCache.clearCart(user.id);

        window.dispatchEvent(new Event("cartUpdated"));

        notification.success({
          message: "¡Pago exitoso!",
          description: "Tu orden ha sido procesada correctamente",
        });

        setTimeout(() => {
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/marketplace/orders", { replace: true });
        }, 3000);
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Hubo un problema al procesar tu orden",
        });
      }
    },
    [user?.id, user?.name, user?.role, navigate, cartKey, updateCart]
  );

  const fetchCart = useCallback(async () => {
    if (fetchInProgressRef.current) return;

    const now = Date.now();
    if (now - lastFetchTimeRef.current < 5000 && !payment_status) {
      return;
    }

    fetchInProgressRef.current = true;
    lastFetchTimeRef.current = Date.now();

    try {
      setLoading(true);

      if (isGuest) {
        const localCart = localStorage.getItem(cartKey);
        if (localCart) {
          const localProducts = JSON.parse(localCart);
          updateCart({ products: localProducts });
        } else {
          updateCart({ products: [] });
        }
      } else {
        const resp = await MarketPlaceService.getCartByClient(user.id);

        if (resp.data) {
          CartCache.setCart(user.id, resp.data);

          const userProducts = getVisibleProducts(resp.data.products);

          updateCart({ products: userProducts });

          localStorage.setItem(cartKey, JSON.stringify(userProducts));

          if (payment_status === "paid") {
            await processSuccessfulPayment(resp.data);
          }
        } else {
          updateCart({ products: [] });
          localStorage.removeItem(cartKey);
          CartCache.clearCart(user.id);
        }
      }
    } catch (error) {
      console.error("Error fetching cart:", error);

      const localCart = localStorage.getItem(cartKey);
      if (localCart) {
        const localProducts = JSON.parse(localCart);
        updateCart({ products: localProducts });
        notification.warning({
          message: "Modo offline",
          description: "Mostrando carrito local",
        });
      } else {
        updateCart({ products: [] });
      }
    } finally {
      setLoading(false);
      initialLoadRef.current = false;
      fetchInProgressRef.current = false;
    }
  }, [
    payment_status,
    processSuccessfulPayment,
    user?.id,
    user?.role,
    cartKey,
    isGuest,
    updateCart,
  ]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleCartUpdate = useCallback(async () => {
    if (isUpdatingRef.current) {
      return;
    }

    isUpdatingRef.current = true;

    try {
      const localCart = localStorage.getItem(cartKey);

      if (localCart) {
        const localProducts = JSON.parse(localCart);
        updateCart({ products: localProducts });

        if (localProducts.length === 0 && user?.id) {
          CartCache.clearCart(user.id);
        }
      } else {
        updateCart({ products: [] });
        if (user?.id) {
          CartCache.clearCart(user.id);
        }
      }

      if (user?.id && !isGuest) {
        const resp = await MarketPlaceService.getCartByClient(user.id);
        if (resp.data) {
          const userProducts = getVisibleProducts(resp.data.products);
          updateCart({ products: userProducts });
        }
      }
    } catch (error) {
      console.error("Error en handleCartUpdate:", error);
    } finally {
      isUpdatingRef.current = false;
    }
  }, [cartKey, user?.id, user?.role, isGuest, updateCart, getVisibleProducts]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === cartKey && !isUpdatingRef.current) {
        try {
          if (e.newValue) {
            const updatedProducts = JSON.parse(e.newValue);
            updateCart({ products: updatedProducts });
            if (user?.id) {
              CartCache.clearCart(user.id);
            }
          } else {
            updateCart({ products: [] });
            if (user?.id) {
              CartCache.clearCart(user.id);
            }
          }
        } catch (error) {
          console.error("Error procesando storage event:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [cartKey, user?.id, updateCart]);

  if (loading) {
    return (
      <LayoutPage>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <p>Cargando carrito...</p>
        </div>
      </LayoutPage>
    );
  }

  return (
    <LayoutPage>
      {checkout_id && order_id && payment_status && (
        <div
          style={{
            margin: "20px 24px",
            padding: "16px",
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: "8px",
          }}
        >
          <Typography.Text strong>Información del pago:</Typography.Text>
          <div style={{ marginTop: "8px" }}>
            <p>
              <strong>Checkout ID:</strong> {checkout_id}
            </p>
            <p>
              <strong>Order ID:</strong> {order_id}
            </p>
            <p>
              <strong>Estado del pago:</strong>
              <span
                style={{
                  color: payment_status === "paid" ? "#52c41a" : "#faad14",
                  fontWeight: "bold",
                  marginLeft: "8px",
                }}
              >
                {payment_status === "paid" ? "Pagado" : payment_status}
              </span>
            </p>
          </div>
        </div>
      )}

      <ShoppingList cart={storeCart} onCartUpdate={handleCartUpdate} />
    </LayoutPage>
  );
}
